/* eslint-disable prettier/prettier */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import { useMaterialUIController, setLayout } from "context";
import CommonFooter from "components/CommonFooter";
import CommonStoreHeader from "components/CommonStoreHeader";
import AppLoader from "components/AppLoader";
import { fetchAllPublicCategories, fetchPublicCategories } from "utils/categoryApi";

import decor1 from "assets/images/home-decor-1.jpg";
import decor2 from "assets/images/home-decor-2.jpg";
import decor3 from "assets/images/home-decor-3.jpg";
import decor4 from "assets/images/home-decor-4.jpeg";
import productPlaceholder from "assets/images/product-placeholder.svg";

const toSlug = (value) =>
  value
    .toLowerCase()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const ghostBanners = [
  {
    promoId: "ghost-1",
    title: " ",
    subtitle: " ",
    image: productPlaceholder,
    tint: "linear-gradient(110deg, rgba(148, 163, 184, 0.18), rgba(100, 116, 139, 0.18))",
    to: "#",
    isGhost: true,
  },
  {
    promoId: "ghost-2",
    title: " ",
    subtitle: " ",
    image: productPlaceholder,
    tint: "linear-gradient(110deg, rgba(148, 163, 184, 0.18), rgba(100, 116, 139, 0.18))",
    to: "#",
    isGhost: true,
  },
  {
    promoId: "ghost-3",
    title: " ",
    subtitle: " ",
    image: productPlaceholder,
    tint: "linear-gradient(110deg, rgba(148, 163, 184, 0.18), rgba(100, 116, 139, 0.18))",
    to: "#",
    isGhost: true,
  },
  {
    promoId: "ghost-4",
    title: " ",
    subtitle: " ",
    image: productPlaceholder,
    tint: "linear-gradient(110deg, rgba(148, 163, 184, 0.18), rgba(100, 116, 139, 0.18))",
    to: "#",
    isGhost: true,
  },
];

const displayFont = '"Poppins", "Segoe UI", "Helvetica Neue", Arial, sans-serif';
const bodyFont = '"Manrope", "Segoe UI", "Helvetica Neue", Arial, sans-serif';

function Home() {
  const [, dispatch] = useMaterialUIController();
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [isLoadingCarouselData, setIsLoadingCarouselData] = useState(true);
  const [categoriesLoadError, setCategoriesLoadError] = useState("");
  const [carouselLoadError, setCarouselLoadError] = useState("");
  const [topCarouselBanners, setTopCarouselBanners] = useState([]);
  const [middleCarouselBanners, setMiddleCarouselBanners] = useState([]);
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [topCarouselStart, setTopCarouselStart] = useState(0);
  const [middleCarouselStart, setMiddleCarouselStart] = useState(0);
  const [isTopCarouselAnimating, setIsTopCarouselAnimating] = useState(true);
  const [isMiddleCarouselAnimating, setIsMiddleCarouselAnimating] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const visibleBannerCount = isMobile ? 1 : isTablet ? 2 : 4;
  const topCarouselData = topCarouselBanners.length > 0 ? topCarouselBanners : ghostBanners;
  const middleCarouselData =
    middleCarouselBanners.length > 0 ? middleCarouselBanners : ghostBanners;
  const collapsedCategoryCount = 14;
  const visibleCategories = showAllCategories
    ? categories
    : categories.slice(0, collapsedCategoryCount);
  const isHomeLoading = isLoadingCategories || isLoadingCarouselData;

  const goToNextBannerSet = (type = "top") => {
    const setAnimating = type === "top" ? setIsTopCarouselAnimating : setIsMiddleCarouselAnimating;
    const setStart = type === "top" ? setTopCarouselStart : setMiddleCarouselStart;
    setAnimating(true);
    setStart((prev) => prev + 1);
  };

  const goToPreviousBannerSet = (type = "top", banners = ghostBanners) => {
    const start = type === "top" ? topCarouselStart : middleCarouselStart;
    const setAnimating = type === "top" ? setIsTopCarouselAnimating : setIsMiddleCarouselAnimating;
    const setStart = type === "top" ? setTopCarouselStart : setMiddleCarouselStart;

    if (start === 0) {
      setAnimating(false);
      setStart(banners.length);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setAnimating(true);
          setStart(banners.length - 1);
        });
      });
      return;
    }

    setAnimating(true);
    setStart((prev) => prev - 1);
  };

  useEffect(() => {
    setLayout(dispatch, "page");

    return () => setLayout(dispatch, "dashboard");
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;

    const fetchCategories = async () => {
      try {
        setCategoriesLoadError("");
        const mappedCategories = await fetchPublicCategories();

        if (isMounted && mappedCategories.length > 0) {
          setCategories(mappedCategories);
        }
      } catch (error) {
        console.error("Failed to load categories for home page:", error);
        if (isMounted) {
          setCategoriesLoadError("We could not load categories right now.");
        }
      } finally {
        if (isMounted) {
          setIsLoadingCategories(false);
        }
      }
    };

    fetchCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadMainCarouselCategories = async () => {
      if (isMounted) setIsLoadingCarouselData(true);
      try {
        if (isMounted) setCarouselLoadError("");
        const allCategories = await fetchAllPublicCategories();
        const mainCategories = allCategories.filter((category) => category?.displayInMain);
        const subCategories = allCategories.filter((category) => category?.displayInSub);

        const mapCategoryToBanner = (category, prefix) => ({
          promoId: `${prefix}-${category.id || category.name}`,
          title: category?.name || "Featured Category",
          subtitle: "Explore items",
          image: category?.imageUrl || decor1,
          tint: "linear-gradient(110deg, rgba(34, 197, 94, 0.7), rgba(20, 184, 166, 0.58))",
          to: `/category/${toSlug(category?.name || "")}`,
        });

        const topBanners = mainCategories.map((category) =>
          mapCategoryToBanner(category, "main-category")
        );
        const midBanners = subCategories.map((category) =>
          mapCategoryToBanner(category, "sub-category")
        );

        if (isMounted) {
          setTopCarouselBanners(topBanners);
          setMiddleCarouselBanners(midBanners);
          setTopCarouselStart(0);
          setMiddleCarouselStart(0);
        }
      } catch (error) {
        console.error("Failed to load main carousel categories:", error);
        if (isMounted) {
          setTopCarouselBanners([]);
          setMiddleCarouselBanners([]);
          setCarouselLoadError("We could not load featured content right now.");
        }
      } finally {
        if (isMounted) {
          setIsLoadingCarouselData(false);
        }
      }
    };

    loadMainCarouselCategories();

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    if (
      topCarouselData.length <= visibleBannerCount &&
      middleCarouselData.length <= visibleBannerCount
    )
      return undefined;

    const timer = setInterval(() => {
      if (topCarouselData.length > visibleBannerCount) {
        setIsTopCarouselAnimating(true);
        setTopCarouselStart((prev) => prev + 1);
      }
      if (middleCarouselData.length > visibleBannerCount) {
        setIsMiddleCarouselAnimating(true);
        setMiddleCarouselStart((prev) => prev + 1);
      }
    }, 1800);

    return () => clearInterval(timer);
  }, [topCarouselData.length, middleCarouselData.length, visibleBannerCount]);

  const renderPromoCarousel = ({
    fullBleed = false,
    singleBanner = false,
    sourceBanners = ghostBanners,
    carouselStart = 0,
    setCarouselStart = () => {},
    isCarouselAnimating = true,
    setIsCarouselAnimating = () => {},
    onPrev = () => {},
    onNext = () => {},
  } = {}) => {
    const itemsPerView = singleBanner ? 1 : visibleBannerCount;
    const shouldLoop = sourceBanners.length > itemsPerView;
    const trackBanners = shouldLoop ? [...sourceBanners, ...sourceBanners] : sourceBanners;
    const slideWidthPercent = 100 / itemsPerView;

    return (
      <Box
        sx={{
          mt: singleBanner ? 0 : 2.5,
          mb: singleBanner ? 3 : 2.5,
          mx: fullBleed ? { xs: -1.5, sm: -2.5, md: -3.5 } : 0,
          width: fullBleed
            ? {
                xs: "calc(100% + 24px)",
                sm: "calc(100% + 40px)",
                md: "calc(100% + 56px)",
              }
            : "100%",
          position: "relative",
        }}
      >
        <IconButton
          aria-label="Previous banners"
          onClick={onPrev}
          disabled={!shouldLoop}
          sx={{
            position: "absolute",
            left: fullBleed ? { xs: 8, md: 16 } : { xs: -4, md: -18 },
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            bgcolor: "rgba(255,255,255,0.95)",
            boxShadow: "0 8px 20px rgba(2, 6, 23, 0.12)",
            "&:hover": { bgcolor: "white" },
          }}
        >
          <ChevronLeftIcon />
        </IconButton>

        <Stack
          direction="row"
          sx={{
            overflow: "hidden",
            px: fullBleed && !singleBanner ? { xs: 1, md: 1.5 } : 0,
          }}
        >
          <Box
            onTransitionEnd={() => {
              if (carouselStart >= sourceBanners.length) {
                setIsCarouselAnimating(false);
                setCarouselStart(0);
              }
            }}
            sx={{
              display: "flex",
              width: "100%",
              transform: `translateX(-${carouselStart * slideWidthPercent}%)`,
              transition: isCarouselAnimating ? "transform 0.55s ease-in-out" : "none",
            }}
          >
            {trackBanners.map((banner, index) => (
              <Box
                key={`${banner.title}-${banner.subtitle}-${index}`}
                sx={{
                  flex: `0 0 ${slideWidthPercent}%`,
                  minWidth: 0,
                  px: singleBanner ? 0 : 0.8,
                  boxSizing: "border-box",
                }}
              >
                <Card
                  component={Link}
                  to={banner?.isGhost ? "#" : banner?.to || `/products?promo=${banner.promoId}`}
                  sx={{
                    height: singleBanner ? { xs: 170, md: 246 } : { xs: 170, md: 198 },
                    borderRadius: singleBanner ? 5.5 : 3,
                    overflow: "hidden",
                    border: "1px solid rgba(148, 163, 184, 0.35)",
                    position: "relative",
                    textDecoration: "none",
                    cursor: "pointer",
                    transition: "transform 0.2s ease, box-shadow 0.2s ease",
                    "&:hover": {
                      transform: "translateY(-3px)",
                      boxShadow: "0 16px 28px rgba(15, 23, 42, 0.14)",
                    },
                  }}
                >
                  <Box
                    component="img"
                    src={banner.image}
                    alt={banner.title}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background: "transparent",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      px: 2,
                    }}
                  >
                    <Typography
                      sx={{
                        color: "white",
                        fontFamily: displayFont,
                        fontWeight: 900,
                        letterSpacing: "0.01em",
                        fontSize: singleBanner
                          ? { xs: "1.35rem", md: "2.2rem" }
                          : { xs: "1.2rem", md: "1.6rem" },
                      }}
                    >
                      {banner.title}
                    </Typography>
                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.96)",
                        fontFamily: bodyFont,
                        fontWeight: 600,
                        mt: 0.5,
                      }}
                    >
                      {banner.subtitle}
                    </Typography>
                  </Box>
                </Card>
              </Box>
            ))}
          </Box>
        </Stack>

        <IconButton
          aria-label="Next banners"
          onClick={onNext}
          disabled={!shouldLoop}
          sx={{
            position: "absolute",
            right: fullBleed ? { xs: 8, md: 16 } : { xs: -4, md: -18 },
            top: "50%",
            transform: "translateY(-50%)",
            zIndex: 2,
            bgcolor: "rgba(255,255,255,0.95)",
            boxShadow: "0 8px 20px rgba(2, 6, 23, 0.12)",
            "&:hover": { bgcolor: "white" },
          }}
        >
          <ChevronRightIcon />
        </IconButton>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        fontFamily: bodyFont,
        background: "#ffffff",
      }}
    >
      <CommonStoreHeader />

      <Box sx={{ pt: 0, pb: 2.5, px: { xs: 1.5, sm: 2.5, md: 3.5 }, width: "100%" }}>
        {isHomeLoading ? (
          <Box
            sx={{
              minHeight: "70vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <AppLoader size={42} />
          </Box>
        ) : categoriesLoadError || carouselLoadError ? (
          <Card
            sx={{
              mx: "auto",
              mt: 2,
              maxWidth: 820,
              borderRadius: 2.2,
              border: "1px solid #fecaca",
              boxShadow: "0 10px 24px rgba(127, 29, 29, 0.08)",
            }}
          >
            <Box sx={{ p: { xs: 2, md: 3 } }}>
              <Typography sx={{ fontWeight: 800, color: "#7f1d1d", fontSize: "1.15rem" }}>
                Unable to load page data
              </Typography>
              <Typography sx={{ mt: 0.8, color: "#475569" }}>
                {categoriesLoadError || carouselLoadError} Please refresh and try again.
              </Typography>
            </Box>
          </Card>
        ) : (
          <>
            {renderPromoCarousel({
              sourceBanners: topCarouselData,
              carouselStart: topCarouselStart,
              setCarouselStart: setTopCarouselStart,
              isCarouselAnimating: isTopCarouselAnimating,
              setIsCarouselAnimating: setIsTopCarouselAnimating,
              onPrev: () => goToPreviousBannerSet("top", topCarouselData),
              onNext: () => goToNextBannerSet("top"),
            })}

            <Box
              sx={{
                mt: 0.5,
                px: { xs: 1.5, md: 2.5 },
                py: 3.5,
                borderRadius: 3.5,
                bgcolor: "rgba(255,255,255,0.84)",
                border: "1px solid rgba(148, 163, 184, 0.2)",
                boxShadow: "0 14px 30px rgba(15, 23, 42, 0.07)",
                backdropFilter: "blur(4px)",
              }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 2.5 }}
              >
                <Box sx={{ width: "100%" }} />
                {!isLoadingCategories && (
                  <Typography
                    onClick={() => setShowAllCategories((prev) => !prev)}
                    sx={{
                      minWidth: "max-content",
                      color: "#166534",
                      fontWeight: 700,
                      fontFamily: bodyFont,
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                  >
                    {showAllCategories ? "View less" : "View more"}
                  </Typography>
                )}
              </Stack>

              {isLoadingCategories ? (
                <Box
                  sx={{ py: 8, display: "flex", justifyContent: "center", alignItems: "center" }}
                >
                  <AppLoader size={38} />
                </Box>
              ) : (
                <Grid
                  container
                  spacing={{ xs: 2.5, md: 3 }}
                  columns={{ xs: 12, sm: 12, md: 12, lg: 14 }}
                >
                  {visibleCategories.map((category) => (
                    <Grid item xs={6} sm={4} md={3} lg={2} key={category.name}>
                      <Stack
                        component={Link}
                        to={`/category/${toSlug(category.name)}`}
                        alignItems="center"
                        spacing={1}
                        sx={{
                          textDecoration: "none",
                          transition: "transform 0.25s ease",
                          "&:hover": {
                            transform: "translateY(-6px)",
                          },
                          "&:hover .category-image-shell": {
                            boxShadow: "0 18px 34px rgba(15, 23, 42, 0.14)",
                            borderColor: "#94a3b8",
                          },
                          "&:hover .category-image": {
                            transform: "scale(1.08) rotate(1deg)",
                          },
                          "&:hover .category-label": {
                            color: "#2E7D32",
                          },
                        }}
                      >
                        <Box
                          className="category-image-shell"
                          sx={{
                            width: { xs: 100, sm: 118, md: 126 },
                            height: { xs: 100, sm: 118, md: 126 },
                            borderRadius: "50%",
                            border: "1px solid #cbd5e1",
                            bgcolor: "white",
                            overflow: "hidden",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            p: 1.2,
                            transition: "all 0.28s ease",
                          }}
                        >
                          <Box
                            className="category-image"
                            component="img"
                            src={category.imageUrl}
                            alt={category.name}
                            onError={(event) => {
                              // Hide broken image icon when URL is invalid/missing.
                              event.currentTarget.style.display = "none";
                            }}
                            sx={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              borderRadius: "50%",
                              transition: "transform 0.35s ease",
                            }}
                          />
                        </Box>
                        <Typography
                          className="category-label"
                          sx={{
                            textAlign: "center",
                            fontFamily: bodyFont,
                            fontWeight: 700,
                            color: "#0f172a",
                            fontSize: "0.96rem",
                            lineHeight: 1.25,
                            minHeight: 40,
                            transition: "color 0.25s ease",
                          }}
                        >
                          {category.name}
                        </Typography>
                      </Stack>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>

            <Box sx={{ position: "relative", mt: 0, mb: 3.1, px: { xs: 0.4, md: 0.8 } }}>
              {renderPromoCarousel({
                fullBleed: false,
                singleBanner: true,
                sourceBanners: middleCarouselData,
                carouselStart: middleCarouselStart,
                setCarouselStart: setMiddleCarouselStart,
                isCarouselAnimating: isMiddleCarouselAnimating,
                setIsCarouselAnimating: setIsMiddleCarouselAnimating,
                onPrev: () => goToPreviousBannerSet("middle", middleCarouselData),
                onNext: () => goToNextBannerSet("middle"),
              })}
            </Box>
          </>
        )}
      </Box>

      <CommonFooter />
    </Box>
  );
}

export default Home;
